import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { months, years } from 'src/app/reporte/modal-ventas/assets/date';
import Swal from 'sweetalert2';

/**
 * Componente de selección de fecha personalizado.
 *
 * Este DatePicker permite seleccionar:
 * - Un año
 * - Un mes
 *
 * Emite un valor final en formato `YYYY-MM`.
 *
 * Se usa principalmente para filtros de fechas en reportes.
 *
 * @component
 * @selector app-date-picker
 * @standalone true
 * @imports CommonModule, FormsModule
 */
@Component({
    selector: 'app-date-picker',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.css'],
})
export class DatePickerComponent implements OnInit {
    @Input() placeholder = 'Seleccionar fecha';
    @Input() dateValue = ''; // Formato YYYY-MM
    @Input() minDate : string | null = null;
    @Input() disabled: boolean = false;
    @Output() dateSelected = new EventEmitter<string>();
    

    // Variables para el selector de fecha
    showDatePicker = false;
    selectingYear = true;
    selectedYear: number = new Date().getFullYear();
    selectedMonth: number = new Date().getMonth() + 1;
    availableMonths: any[] = [];

    years = years;
    months = months;

    ngOnInit() {   
        this.years = years.filter((year) => year >= 2025);
        if (this.dateValue) {
            // Parsear el string YYYY-MM            
            const [year, month] = this.dateValue.split('-').map(Number);
            this.selectedYear = year;  
                          
            this.selectedMonth = month;            
        } else {
            this.availableMonths = this.months;     
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['minDate']) {
          this.updateMonths();
        }
    }

    /**
     * Devuelve la fecha formateada en texto (ejemplo: "Enero 2025").
     */
    get formattedDate(): string {
        if (!this.dateValue) return '';
        const [year, month] = this.dateValue.split('-').map(Number);
        const monthObj = this.months.find((m) => m.number === month);
        return `${monthObj?.name} ${year}`;
    }

    /**
     * Abre el selector de fecha y establece valores iniciales.
     */
    openDatePicker() {
        if (this.disabled) return;
        this.selectingYear = true;
        this.showDatePicker = true;

        // Si ya hay una fecha seleccionada, usarla como valores iniciales
        if(this.minDate) {
            const [minYear, minMonth] = this.minDate.split('-').map(Number);
            this.years = years.filter((year) => year >= minYear);
            
            this.selectedYear = minYear;
            this.upadateYears()
            this.updateMonths();
        }
        if (this.dateValue) {
            const [year, month] = this.dateValue.split('-').map(Number);
            this.selectedYear = year;
            this.selectedMonth = month;
            this.upadateYears();
            this.updateMonths();
        } else {
            // Valores por defecto
            this.selectedYear = new Date().getFullYear();
            this.selectedMonth = new Date().getMonth() + 1;
        }
    }

    /**
     * Cierra el selector de fecha.
     */
    closeDatePicker() {
        this.showDatePicker = false;
        this.selectingYear = true;
    }

    /**
     * Al seleccionar un año, cambia automáticamente a la selección de mes.
     */
    onYearSelect() {
        setTimeout(() => {
            this.selectingYear = false; 
            this.updateMonths();           
        }, 300);
    }

    /** Alternativa manual para cambiar a selección de mes */
    selectYear() {
        this.selectingYear = false;
    }

    /** Regresa a la selección de año */
    backToYearSelection() {
        this.selectingYear = true;
    }

    /**
     * Finaliza la selección de fecha, emitiendo un valor en formato YYYY-MM.
     */
    selectDate() {
        // Formatear como YYYY-MM
        const formattedMonth = this.selectedMonth.toString().padStart(2, '0');
        const formattedDate = `${this.selectedYear}-${formattedMonth}`;
        if (this.minDate && formattedDate < this.minDate) {
            Swal.fire(
              'Error',
              `Fecha final no puede ser menor a la fecha inicial ${formattedDate} < ${this.minDate}`
            );
            return;
        }
        
        this.dateSelected.emit(formattedDate);
        this.closeDatePicker();
    }

    upadateYears() {
        if(this.minDate) {
            const [minYear, minMonth] = this.minDate.split('-').map(Number);
            this.years = years.filter((year) => year >= minYear);
            this.updateMonths();
        } else {
            this.years = years.filter((year) => year >= 2025);
            this.updateMonths();
        }
    }

    updateMonths() {
        if (this.minDate) {
            const [minYear, minMonth] = this.minDate.split('-').map(Number);
            this.availableMonths =
                Number(this.selectedYear) == Number(minYear)
                    ? this.months.filter(month => month.number >= minMonth)
                    : this.months;
        } else {
            this.availableMonths = this.months;
        }
        if (!this.availableMonths.some(m => m.number === this.selectedMonth)) {
            this.selectedMonth = this.availableMonths[0]?.number;
        }
    }
}
